/**
 * Credits — Plan 07-04 Task 1 GREEN.
 *
 * Closes ABOUT-02 (Credits page — archives, collaborators, historians, licenses)
 * and surfaces the explicit AI-narration disclosure row required by the
 * London Charter transparency principle + Phase 7 AUDIO-03 commitment.
 *
 * Content is authored in `app/src/content/credits.json` as a pure data file
 * (sections × rows of name/role pairs). The renderer here is a shallow map;
 * adding a new archive or collaborator is a JSON edit, not a code edit.
 *
 * Structure: every row is a <dt>/<dd> pair inside a <dl> — correct semantics
 * for a name-to-description association, and axe-core-friendly.
 *
 * AESTHETIC-BRIEF Rule 7 chrome via the shared PeriodChromeStyles tokens.
 */
import { Link } from 'react-router-dom';
import credits from '../content/credits.json';
import { typography, periodChrome } from '../ui/PeriodChromeStyles';

type CreditsRow = {
  name: string;
  role: string;
};

type CreditsSection = {
  id: string;
  heading: string;
  rows: CreditsRow[];
};

type CreditsDocument = {
  sections: CreditsSection[];
};

export function Credits() {
  const doc = credits as CreditsDocument;
  return (
    <main role="main" aria-labelledby="credits-title" style={S.main}>
      <div style={S.inner}>
        <header style={S.header}>
          <nav aria-label="Site navigation" style={S.nav}>
            <Link to="/" style={S.navLink}>
              Experience
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/about" style={S.navLink}>
              About
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/sources" style={S.navLink}>
              Sources
            </Link>
          </nav>
          <h1 id="credits-title" style={S.h1}>
            Credits
          </h1>
        </header>

        {doc.sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`${section.id}-heading`}
            style={S.section}
          >
            <h2 id={`${section.id}-heading`} style={S.h2}>
              {section.heading}
            </h2>
            <dl style={S.dl}>
              {section.rows.map((row, i) => (
                <div key={i} style={S.row}>
                  <dt style={S.dt}>{row.name}</dt>
                  <dd style={S.dd}>{row.role}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </main>
  );
}

const S: Record<string, React.CSSProperties> = {
  main: {
    minHeight: '100vh',
    background: periodChrome.cream,
    color: periodChrome.espresso,
    fontFamily: typography.serif,
    padding: '3rem 1.5rem',
    boxSizing: 'border-box',
    lineHeight: 1.6,
  },
  inner: {
    maxWidth: '72ch',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
    borderBottom: `1px solid ${periodChrome.ochre}`,
    paddingBottom: '1rem',
  },
  nav: {
    marginBottom: '1rem',
    fontSize: '0.95em',
    opacity: 0.85,
  },
  navLink: {
    color: periodChrome.espresso,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
  },
  navSep: {
    margin: '0 0.5rem',
    color: periodChrome.ochre,
  },
  h1: {
    fontWeight: 400,
    fontSize: '2.25rem',
    letterSpacing: '0.04em',
    margin: '0.5rem 0',
    lineHeight: 1.2,
  },
  section: {
    marginBottom: '2rem',
  },
  h2: {
    fontWeight: 400,
    fontSize: '1.35rem',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    letterSpacing: '0.02em',
  },
  dl: {
    margin: 0,
  },
  row: {
    marginBottom: '0.85rem',
  },
  dt: {
    fontWeight: 600,
    fontSize: '1.05rem',
  },
  dd: {
    margin: '0.2rem 0 0 1rem',
    fontSize: '1.0625rem',
    lineHeight: 1.55,
  },
};
